<script setup lang="ts">
import { computed, getCurrentInstance, ref, useAttrs, useId, type PropType } from 'vue'

import { useI18n } from 'vue-i18n'
import { useDefaults } from 'vuetify'
import { makeVBtnProps, VBtn } from 'vuetify/lib/components/VBtn/VBtn.mjs'
import { toKebabCase } from 'vuetify/lib/util/helpers.mjs'

import { localeKey } from '@/utils/localeKey'

defineOptions({
  inheritAttrs: false,
})

const _props = defineProps({
  /**
   * Locale key resolved as `tooltip.<key>` then `common.<key>`. A dotted key from another
   * namespace (`saved_views.pin`) also resolves, so a `tooltip.saved_views.pin` override still wins.
   */
  tooltip: {
    type: String,
    default: undefined,
  },
  /** Ready-made text (already translated, e.g. with interpolation). Wins over `tooltip`. */
  tooltipText: {
    type: String,
    default: undefined,
  },
  /** Overrides the scoped default `BTooltipBtn.VTooltip.location` in plugins/vuetify.ts. */
  tooltipLocation: {
    type: String as PropType<'top' | 'bottom' | 'start' | 'end'>,
    default: undefined,
  },
  /** Consumer's accessible name. Wins over the icon-only fallback derived from `tooltip`. */
  ariaLabel: {
    type: String,
    default: undefined,
  },
  /** Extra id(s) the button is described by (e.g. a second, persistent tooltip). Merged with ours. */
  ariaDescribedby: {
    type: String,
    default: undefined,
  },
  ...makeVBtnProps(),
})
const props = useDefaults(_props)
const slots = defineSlots<{
  default?: () => unknown
  prepend?: () => unknown
  append?: () => unknown
  loader?: () => unknown
  tooltip?: (props: { text: string }) => unknown
}>()

const i18n = useI18n()
const attrs = useAttrs()
const vm = getCurrentInstance()
const tooltipId = useId()

/**
 * Forward only the props the consumer set. Materialised defaults from makeVBtnProps would
 * otherwise override defaults provided by parents, e.g. VToolbar's `variant: 'text'`.
 * Called from the template, not a computed, because vnode.props is not reactive.
 * `filterProps` leaves out `class` and `style`, so the template binds those itself.
 */
function forwardedBtnProps() {
  const vnodeProps = vm?.vnode.props ?? {}
  const all = VBtn.filterProps(props) as Record<string, unknown>
  return Object.fromEntries(
    Object.entries(all).filter(([key]) => vnodeProps[key] !== undefined || vnodeProps[toKebabCase(key)] !== undefined)
  )
}

function isNonEmpty(value: string | undefined): value is string {
  return value != null && value !== ''
}

const resolvedText = computed<string | undefined>(() => {
  if (isNonEmpty(props.tooltipText)) return props.tooltipText
  if (isNonEmpty(props.tooltip)) return localeKey(props.tooltip, ['tooltip', 'common'], true, i18n)
  return undefined
})
const hasTooltip = computed(() => resolvedText.value != null)

/**
 * Short label used as the icon-only aria-label fallback. Deliberately skips `tooltip.*` so a
 * long `tooltip.<key>` description never becomes the accessible *name* — it stays a description.
 */
const fallbackName = computed<string | undefined>(() => {
  if (!isNonEmpty(props.tooltip)) return resolvedText.value
  const label = localeKey(props.tooltip, ['common'], true, i18n)
  return label !== props.tooltip ? label : resolvedText.value
})

const iconValue = computed(() =>
  props.icon !== true && props.icon !== false && props.icon != null ? props.icon : undefined
)
const isIconOnly = computed(() => props.icon != null && props.icon !== false)
const resolvedAriaLabel = computed(() => {
  if (isNonEmpty(props.ariaLabel)) return props.ariaLabel
  return isIconOnly.value ? fallbackName.value : undefined
})

const accessibleName = computed(
  () => resolvedAriaLabel.value ?? (typeof props.text === 'string' ? props.text : undefined)
)

/** Identical name and description would be read twice by screen readers ("Sök, Sök"). */
const tooltipRepeatsName = computed(
  () => slots.tooltip == null && resolvedText.value != null && resolvedText.value === accessibleName.value
)

const describedBy = computed<string | undefined>(() => {
  const ids = [
    isNonEmpty(props.ariaDescribedby) ? props.ariaDescribedby : undefined,
    hasTooltip.value && !tooltipRepeatsName.value ? tooltipId : undefined,
  ].filter((id) => id != null)
  return ids.length > 0 ? ids.join(' ') : undefined
})

// VTooltip writes aria-describedby on the button itself (bindProps), so it must own the attribute
// whenever a tooltip exists; Vue and bindProps writing the same attribute would overwrite each other.
const activatorProps = computed(() => ({ 'aria-describedby': describedBy.value }))
const btnDescribedBy = computed(() => (hasTooltip.value ? undefined : describedBy.value))

// A click opens what the button is for (drawer, dialog, menu), and the hover label must not linger over it.
// `disabled` closes the tooltip at once and blocks an open whose delay is still running; the pointer or
// focus coming back re-arms it. A button without a click action, such as an info icon, keeps its tooltip,
// since a tap on a touch screen is a click too.
const isTooltipSuppressed = ref(false)

function suppressTooltipOnAction() {
  if (attrs.onClick != null || props.to != null || props.href != null) isTooltipSuppressed.value = true
}
</script>

<template>
  <v-btn
    v-bind="{ ...forwardedBtnProps(), ...$attrs }"
    :class="props.class"
    :style="props.style"
    :aria-label="resolvedAriaLabel"
    :aria-describedby="btnDescribedBy"
    @click="suppressTooltipOnAction"
    @mouseenter="isTooltipSuppressed = false"
    @focusin="isTooltipSuppressed = false"
  >
    <template
      v-if="slots.prepend"
      #prepend
    >
      <slot name="prepend" />
    </template>
    <slot>
      <v-icon
        v-if="iconValue != null"
        :icon="iconValue"
      />
      <template v-else>{{ props.text }}</template>
    </slot>
    <v-tooltip
      v-if="hasTooltip"
      :id="tooltipId"
      :disabled="isTooltipSuppressed"
      :text="resolvedText"
      :location="props.tooltipLocation"
      :activator-props="activatorProps"
      activator="parent"
    >
      <template
        v-if="slots.tooltip"
        #default
      >
        <slot
          name="tooltip"
          :text="resolvedText ?? ''"
        />
      </template>
    </v-tooltip>
    <template
      v-if="slots.append"
      #append
    >
      <slot name="append" />
    </template>
    <template
      v-if="slots.loader"
      #loader
    >
      <slot name="loader" />
    </template>
  </v-btn>
</template>
